import { useState } from "react";
import { useTrocarCertificado, type AlvoDoCertificado } from "@/api/consultas";
import { useAvisos } from "@/componentes/Avisos";
import { Campo } from "@/componentes/Campo";
import { CampoDeArquivo } from "@/componentes/CampoDeArquivo";
import { Confirmacao } from "@/componentes/Confirmacao";
import { ModalDeCadastro } from "@/componentes/ModalDeCadastro";
import { useSessao } from "@/sessao/useSessao";
import {
  erroDoArquivo,
  errosDoEnvio,
  EXTENSOES_DO_CERTIFICADO,
  formularioDoEnvio,
  validarEnvio,
  type CampoDoEnvio,
  type ErrosDoEnvio,
} from "./certificado";

/*
 * O envio do certificado, aberto por `?abrir=certificado`.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A SENHA SAI DO ESTADO ANTES DA VIAGEM, E O MODAL FECHADO NÃO LEMBRA NADA.│
 * │                                                                          │
 * │ Arquivo e senha vivem nos `useState` daqui, e só. Quem abre monta este   │
 * │ componente, e quem fecha o desmonta: não há reset, porque não sobra      │
 * │ estado para zerar. A senha some no envio, e por isso qualquer recusa     │
 * │ devolve o campo vazio. O arquivo fica até o fim, para quem errou só a    │
 * │ senha não escolher de novo.                                              │
 * │                                                                          │
 * │ O certificado vai para a TecnoSpeed de produção, inclusive de dev. A     │
 * │ confirmação diz o efeito antes do envio, e o modal não explica nada.     │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export function TrocarCertificado({
  alvo,
  nome,
  troca,
  aoFechar,
}: {
  alvo: AlvoDoCertificado;
  /** O dado sob o título: `756/123456`, ou a razão social no nível cedente. */
  nome: string;
  /** `true` substitui o que já está lá. O texto do efeito é outro sem linha. */
  troca: boolean;
  aoFechar: () => void;
}) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const envio = useTrocarCertificado(cliente, alvo);

  const [arquivo, setArquivo] = useState<File | null>(null);
  const [senha, setSenha] = useState("");
  const [apelido, setApelido] = useState("");
  const [erros, setErros] = useState<ErrosDoEnvio>({});
  const [erroGeral, setErroGeral] = useState<string | undefined>(undefined);
  const [confirmando, setConfirmando] = useState(false);

  const verbo = troca ? "Trocar certificado" : "Enviar certificado";
  const doAlvo = alvo.nivel === "convenio" ? `do convênio ${nome}` : `do cedente ${nome}`;

  function limparErro(campo: CampoDoEnvio) {
    setErros(({ [campo]: _resolvido, ...restantes }) => restantes);
    setErroGeral(undefined);
  }

  function escolherArquivo(escolhido: File | null): boolean {
    const recusa = escolhido === null ? undefined : erroDoArquivo(escolhido);
    setArquivo(recusa === undefined ? escolhido : null);
    limparErro("arquivo");
    if (recusa === undefined) return true;

    setErros((atuais) => ({ ...atuais, arquivo: recusa }));
    return false;
  }

  function pedirConfirmacao() {
    setErroGeral(undefined);
    const recusas = validarEnvio({ arquivo, senha, apelido });
    // O arquivo recusado na escolha sai do campo, e o motivo vale mais que "escolha o arquivo".
    if (arquivo === null && erros.arquivo !== undefined) recusas.arquivo = erros.arquivo;
    setErros(recusas);
    if (Object.keys(recusas).length === 0) setConfirmando(true);
  }

  async function enviar() {
    if (arquivo === null) return;

    const formulario = formularioDoEnvio(arquivo, senha, apelido);
    setSenha("");

    try {
      await envio.enviar(formulario);
    } catch (falha) {
      // A recusa volta para o formulário, e não para a confirmação: é lá que está o campo.
      const { porCampo, geral } = errosDoEnvio(falha);
      setErros(porCampo);
      setErroGeral(geral);
      setConfirmando(false);
      return;
    }

    avisos.feito("Certificado enviado");
    aoFechar();
  }

  return (
    <>
      <ModalDeCadastro
        aberto
        aoFechar={aoFechar}
        titulo={verbo}
        descricao={nome}
        aoEnviar={pedirConfirmacao}
        enviando={envio.isPending}
        erro={erroGeral}
        rotuloDeEnvio={verbo}
      >
        <CampoDeArquivo
          rotulo="Arquivo"
          dica="Arquivo .pfx ou .p12, até 128 KiB."
          erro={erros.arquivo}
          accept={EXTENSOES_DO_CERTIFICADO}
          aoEscolher={escolherArquivo}
        />
        <Campo
          rotulo="Senha"
          dica="De 1 a 20 caracteres. Repassada à TecnoSpeed, sem ficar guardada aqui."
          type="password"
          autoComplete="new-password"
          value={senha}
          onChange={(evento) => {
            setSenha(evento.target.value);
            limparErro("senha");
          }}
          erro={erros.senha}
          aria-required
        />
        <Campo
          rotulo="Apelido"
          dica={
            alvo.nivel === "convenio"
              ? "Até 250 caracteres. Sem apelido, vale o número do convênio."
              : "Até 250 caracteres."
          }
          value={apelido}
          onChange={(evento) => {
            setApelido(evento.target.value);
            limparErro("apelido");
          }}
          erro={erros.apelido}
          autoComplete="off"
        />
      </ModalDeCadastro>

      <Confirmacao
        pedido={
          confirmando
            ? {
                severidade: "destrutiva",
                titulo: `${troca ? "Trocar" : "Enviar"} o certificado ${doAlvo}?`,
                corpo: troca
                  ? "Substitui na TecnoSpeed, em produção, o certificado que a emissão usa."
                  : "Grava na TecnoSpeed, em produção, o certificado que a emissão vai usar.",
                verbo,
              }
            : null
        }
        aoConfirmar={enviar}
        aoCancelar={() => setConfirmando(false)}
      />
    </>
  );
}
